import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiContentSaveOutline } from "@mdi/js";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  getShowChangeMaterialRequestQueryKey,
  useShowChangeMaterialRequest,
  useUpdateChangeMaterialRequest,
} from "../../../../../__generated__/orval/endpoints/change-material-requests/change-material-requests";
import { ConfirmationModal } from "../../../../../components/molecules/ConfirmationModal";
import { EditChangeMaterialRequestForm } from "../-internal-components/EditChangeMaterialRequestForm";
import {
  buildUpdateChangeMaterialRequestPayload,
  type EditChangeMaterialRequestFormSchema,
  editChangeMaterialRequestFormSchema,
} from "../-schemas/editChangeMaterialRequestSchema";

type EditChangeMaterialRequestFormContainerProps = {
  changeMaterialRequestId: string;
  defaultValues: EditChangeMaterialRequestFormSchema;
  id: number;
  title: string;
};

const EditChangeMaterialRequestFormContainer: React.FC<
  EditChangeMaterialRequestFormContainerProps
> = ({ changeMaterialRequestId, defaultValues, id, title }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [formData, setFormData] =
    useState<EditChangeMaterialRequestFormSchema | null>(null);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues,
    resolver: zodResolver(editChangeMaterialRequestFormSchema),
    shouldUnregister: true,
  });

  const { mutate, isPending } = useUpdateChangeMaterialRequest({
    mutation: {
      onError: () => {
        enqueueSnackbar(t("mmRequestChild.updateErrorMessage"), {
          variant: "error",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getShowChangeMaterialRequestQueryKey(id),
        });
        enqueueSnackbar(t("mmRequestChild.updateSuccessMessage"), {
          variant: "success",
        });
        navigate({
          params: { changeMaterialRequestId },
          to: "/vbuyer/change_material_requests/$changeMaterialRequestId",
        });
      },
    },
  });

  const onSubmit: SubmitHandler<EditChangeMaterialRequestFormSchema> =
    useCallback((values) => {
      setFormData(values);
      setOpenConfirmModal(true);
    }, []);

  const handleConfirm = useCallback(() => {
    if (!formData) return;

    setOpenConfirmModal(false);

    const payload = buildUpdateChangeMaterialRequestPayload(formData);

    mutate({
      data: payload,
      id,
    });
  }, [formData, id, mutate]);

  return (
    <PageContainer
      backTo={`/vbuyer/change_material_requests/${changeMaterialRequestId}`}
      showBackButton
      title={title}
    >
      <AppCard>
        <EditChangeMaterialRequestForm
          control={control}
          onSubmit={handleSubmit(onSubmit)}
          setValue={setValue}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              color="success"
              loading={isPending}
              startIcon={<Icon icon={mdiContentSaveOutline} />}
              type="submit"
            >
              {t("common.save")}
            </Button>
          </Box>
        </EditChangeMaterialRequestForm>
      </AppCard>

      <ConfirmationModal
        handleOk={handleConfirm}
        message={t("mmRequestChild.updateModalMessage")}
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        title={t("mmRequestChild.updateModalTitle")}
      />
    </PageContainer>
  );
};

const EditChangeMaterialRequest: React.FC = () => {
  const { t } = useTranslation();
  const { changeMaterialRequestId } = Route.useParams();
  const id = Number(changeMaterialRequestId);

  const { data, isLoading, isError } = useShowChangeMaterialRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const mmRequestId = data?.mm_request?.id;
  const title = mmRequestId
    ? `${t("common.changeMaterialRequest")} #${mmRequestId}`
    : t("common.changeMaterialRequest");

  if (isLoading || !data?.mm_request) {
    return (
      <PageContainer
        backTo={`/vbuyer/change_material_requests/${changeMaterialRequestId}`}
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  const mmRequest = data.mm_request as typeof data.mm_request & {
    previous_ticket_id?: number | null;
  };

  const matnumber = Array.isArray(mmRequest.matnumber)
    ? mmRequest.matnumber.filter((value) => value.length > 0)
    : [];

  const defaultValues: EditChangeMaterialRequestFormSchema = {
    change_type:
      (
        data as {
          change_type?: EditChangeMaterialRequestFormSchema["change_type"];
        }
      ).change_type ?? null,
    description: mmRequest.description ?? "",
    identified_problem_type: data.identified_problem_type ?? null,
    matnumber,
    previous_ticket: mmRequest.previous_ticket_id
      ? String(mmRequest.previous_ticket_id)
      : "",
    requester_area: mmRequest.requester_area
      ? { id: mmRequest.requester_area, name: mmRequest.requester_area }
      : null,
    requester_field: mmRequest.requester_field
      ? { id: mmRequest.requester_field, name: mmRequest.requester_field }
      : null,
    ritm: data.ritm ?? "",
    serial_number: data.serial_number ?? "",
    subject: mmRequest.subject ?? "",
    value_current: data.value_current ?? "",
    value_new: data.value_new ?? "",
  };

  return (
    <EditChangeMaterialRequestFormContainer
      changeMaterialRequestId={changeMaterialRequestId}
      defaultValues={defaultValues}
      id={id}
      title={title}
    />
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/change_material_requests/$changeMaterialRequestId/edit",
)({
  component: EditChangeMaterialRequest,
});
